import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import { api } from '../../api';
import Swal from 'sweetalert2';

// OWASP Password Rules
const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters long', regex: /.{8,}/ },
  { id: 'uppercase', label: 'Contains at least one uppercase letter (A-Z)', regex: /[A-Z]/ },
  { id: 'lowercase', label: 'Contains at least one lowercase letter (a-z)', regex: /[a-z]/ },
  { id: 'number', label: 'Contains at least one number (0-9)', regex: /[0-9]/ },
  { id: 'special', label: 'Contains at least one special character (!@#$%^&*)', regex: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/ },
];

function MyProfile() {
  const [loading, setLoading] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false,
  });

  const [passwordsMatch, setPasswordsMatch] = useState(true);

  // Load user profile
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      
      // Fetch user profile from contacts API
      const response = await api.contacts.getMyProfile();
      const userData = response?.data?.data || response?.data;
      
      console.log('Fetched user profile from API:', userData);
      setUserInfo(userData);
      
      setProfileData({
        firstName: userData.firstName || '',
        middleName: userData.middleName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
      });
    } catch (error) {
      console.error('Error loading user profile:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load user profile',
      });
    } finally {
      setLoading(false);
    }
  };

  // Validate password against OWASP rules
  const validatePassword = (password) => {
    const validation = {
      length: PASSWORD_RULES[0].regex.test(password),
      uppercase: PASSWORD_RULES[1].regex.test(password),
      lowercase: PASSWORD_RULES[2].regex.test(password),
      number: PASSWORD_RULES[3].regex.test(password),
      special: PASSWORD_RULES[4].regex.test(password),
    };
    setPasswordValidation(validation);
    return Object.values(validation).every(v => v);
  };

  // Handle new password change
  const handleNewPasswordChange = (e) => {
    const newPassword = e.target.value;
    setPasswordData(prev => ({ ...prev, newPassword }));
    validatePassword(newPassword);
    
    // Check if passwords match
    if (passwordData.confirmPassword) {
      setPasswordsMatch(newPassword === passwordData.confirmPassword);
    }
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    const confirmPassword = e.target.value;
    setPasswordData(prev => ({ ...prev, confirmPassword }));
    setPasswordsMatch(passwordData.newPassword === confirmPassword);
  };

  // Handle profile update
  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      const updateData = {
        firstName: profileData.firstName,
        middleName: profileData.middleName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
      };
      
      const response = await api.contacts.updateMyProfile(updateData);
      
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Profile updated successfully',
        timer: 2000,
        showConfirmButton: false,
      });
      
      loadUserProfile();
    } catch (error) {
      console.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();

    // Validate all fields are filled
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: 'Please fill in all password fields',
      });
      return;
    }

    // Validate new password meets OWASP requirements
    const isValid = Object.values(passwordValidation).every(v => v);
    if (!isValid) {
      Swal.fire({
        icon: 'warning',
        title: 'Weak Password',
        text: 'Please ensure your password meets all security requirements',
      });
      return;
    }

    // Check if passwords match
    if (!passwordsMatch) {
      Swal.fire({
        icon: 'warning',
        title: 'Password Mismatch',
        text: 'New password and confirm password do not match',
      });
      return;
    }

    try {
      setLoading(true);
      await api.auth.changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Password changed successfully',
        timer: 2000,
        showConfirmButton: false,
      });

      // Reset password form
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordValidation({
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
      });
      setPasswordsMatch(true);
    } catch (error) {
      console.error('Error changing password:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to change password',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          {/* Profile Information Card */}
          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <MDTypography variant="h6" color="white">
                  Profile Information
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <form onSubmit={handleProfileUpdate}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="First Name"
                        value={profileData.firstName}
                        onChange={(e) => setProfileData(prev => ({ ...prev, firstName: e.target.value }))}
                        disabled={loading}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Middle Name"
                        value={profileData.middleName}
                        onChange={(e) => setProfileData(prev => ({ ...prev, middleName: e.target.value }))}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        value={profileData.lastName}
                        onChange={(e) => setProfileData(prev => ({ ...prev, lastName: e.target.value }))}
                        disabled={loading}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                        disabled={loading}
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Phone"
                        value={profileData.phone}
                        onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                        disabled={loading}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MDBox display="flex" justifyContent="flex-end">
                        <MDButton
                          variant="gradient"
                          color="dark"
                          type="submit"
                          disabled={loading}
                        >
                          Update Profile
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>

          {/* Change Password Card */}
          <Grid item xs={12} lg={6}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="logo"
                borderRadius="lg"
                coloredShadow="logo"
              >
                <MDTypography variant="h6" color="white">
                  Change Password
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <form onSubmit={handlePasswordChange}>
                  <Grid container spacing={3}>
                    {/* Current Password */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Current Password"
                        type={showPassword.current ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        disabled={loading}
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(prev => ({ ...prev, current: !prev.current }))}
                                edge="end"
                              >
                                {showPassword.current ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* New Password */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="New Password"
                        type={showPassword.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={handleNewPasswordChange}
                        disabled={loading}
                        required
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(prev => ({ ...prev, new: !prev.new }))}
                                edge="end"
                              >
                                {showPassword.new ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Confirm Password */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        type={showPassword.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={handleConfirmPasswordChange}
                        disabled={loading}
                        required
                        error={!passwordsMatch && passwordData.confirmPassword !== ''}
                        helperText={!passwordsMatch && passwordData.confirmPassword !== '' ? 'Passwords do not match' : ''}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(prev => ({ ...prev, confirm: !prev.confirm }))}
                                edge="end"
                              >
                                {showPassword.confirm ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>

                    {/* Password Rules */}
                    {passwordData.newPassword && (
                      <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
                            Password Security Requirements:
                          </MDTypography>
                          <List dense>
                            {PASSWORD_RULES.map((rule) => (
                              <ListItem key={rule.id} sx={{ py: 0, px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  {passwordValidation[rule.id] ? (
                                    <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />
                                  ) : (
                                    <Cancel sx={{ color: 'error.main', fontSize: 18 }} />
                                  )}
                                </ListItemIcon>
                                <ListItemText
                                  primary={rule.label}
                                  primaryTypographyProps={{
                                    variant: 'caption',
                                    sx: {
                                      color: passwordValidation[rule.id] ? 'success.main' : 'error.main',
                                      fontWeight: passwordValidation[rule.id] ? 'bold' : 'normal',
                                    },
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Alert>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <MDBox display="flex" justifyContent="flex-end">
                        <MDButton
                          variant="gradient"
                          color="dark"
                          type="submit"
                          disabled={loading}
                        >
                          Change Password
                        </MDButton>
                      </MDBox>
                    </Grid>
                  </Grid>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default MyProfile;
